import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { pw9336Api, QueryRequest, QueryResponse } from '../../../services/pw9336Api';

export interface PW9336QueryState {
  loading: boolean;
  result: QueryResponse | null;
  queried: boolean;
  noData: boolean;
  defaultYear: number;
  defaultMonth: string;
}

export interface PW9336QueryActions {
  runQuery: (req: QueryRequest) => Promise<void>;
  reset: () => void;
}

export function usePW9336Query(): PW9336QueryState & PW9336QueryActions {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<QueryResponse | null>(null);
  const [queried, setQueried] = useState(false);
  const [noData, setNoData] = useState(false);
  const [defaultYear, setDefaultYear] = useState(new Date().getFullYear());
  const [defaultMonth, setDefaultMonth] = useState(
    String(new Date().getMonth() + 1).padStart(2, '0')
  );

  // Load defaults from server (BR-I02)
  useEffect(() => {
    pw9336Api.getDefaults().then((resp) => {
      if (resp.year >= 1990) {
        setDefaultYear(resp.year);
      }
      if (resp.month) {
        setDefaultMonth(resp.month);
      }
    }).catch(() => {
      // Keep client-side defaults on error
    });
  }, []);

  const runQuery = useCallback(async (req: QueryRequest) => {
    setLoading(true);
    setQueried(false);
    setNoData(false);
    try {
      const resp = await pw9336Api.query(req);
      if (resp.batches && resp.batches.length > 0) {
        setResult(resp);
        setNoData(false);
      } else {
        setResult(null);
        setNoData(true);
        message.info('查無符合條件的薪資資料');
      }
      setQueried(true);
    } catch {
      message.error('查詢失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setResult(null);
    setQueried(false);
    setNoData(false);
  }, []);

  return { loading, result, queried, noData, defaultYear, defaultMonth, runQuery, reset };
}
